import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { createUsedMachine, updateUsedMachine, getUsedMachines } from '../services/apiService';
import ImageUploader from '../components/ImageUploader';
import GalleryUploader from '../components/GalleryUploader';

const UsedMachineForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    type: 'CO2',
    power: '',
    workArea: '',
    year: '',
    condition: 'Très bon état',
    hours: '',
    price: '',
    originalPrice: '',
    soldPrice: '',
    soldDate: '',
    image: '',
    gallery: [], // Tableau d'images (Base64 ou URL)
    description: '',
    included: '',
    status: 'available'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      fetchMachine();
    }
  }, [isEdit, id]);

  const fetchMachine = async () => {
    try {
      const machines = await getUsedMachines();
      const machine = machines.find(m => m.id === id);
      if (machine) {
        setFormData({
          ...machine,
          included: machine.included?.join('\n') || '',
          gallery: Array.isArray(machine.gallery) ? machine.gallery : []
        });
      }
    } catch (error) {
      console.error('Error fetching used machine:', error);
      setError('Erreur lors du chargement');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const machineData = {
        ...formData,
        included: formData.included.split('\n').filter(i => i.trim()),
        gallery: Array.isArray(formData.gallery) ? formData.gallery : []
      };

      if (isEdit) {
        await updateUsedMachine(id, machineData);
      } else {
        await createUsedMachine(machineData);
      }

      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Error saving:', err);
      setError('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au dashboard
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
            {isEdit ? 'Modifier l\'occasion' : 'Ajouter une occasion'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Nom *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="CO2">CO2</option>
                  <option value="Fibre">Fibre</option>
                  <option value="Mixte">Mixte</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Puissance *</label>
                <input
                  type="text"
                  name="power"
                  value={formData.power}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="60W"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Zone de travail *</label>
                <input
                  type="text"
                  name="workArea"
                  value={formData.workArea}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="600 x 400 mm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Statut *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="available">Disponible</option>
                  <option value="sold">Vendue</option>
                </select>
              </div>

              {formData.status === 'available' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Année</label>
                    <input
                      type="text"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="2022"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">État</label>
                    <input
                      type="text"
                      name="condition"
                      value={formData.condition}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="Très bon état"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Heures d'utilisation</label>
                    <input
                      type="text"
                      name="hours"
                      value={formData.hours}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="1200h d'utilisation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Prix *</label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      required={formData.status === 'available'}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="4 500 €"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Prix d'origine</label>
                    <input
                      type="text"
                      name="originalPrice"
                      value={formData.originalPrice}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="7 200 €"
                    />
                  </div>
                </>
              )}

              {formData.status === 'sold' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Prix de vente *</label>
                    <input
                      type="text"
                      name="soldPrice"
                      value={formData.soldPrice}
                      onChange={handleChange}
                      required={formData.status === 'sold'}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="8 900 €"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Date de vente *</label>
                    <input
                      type="text"
                      name="soldDate"
                      value={formData.soldDate}
                      onChange={handleChange}
                      required={formData.status === 'sold'}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                      placeholder="Décembre 2024"
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <ImageUploader
                label="Image principale"
                required
                value={formData.image}
                onChange={(v) => setFormData((prev) => ({ ...prev, image: v }))}
              />
            </div>

            <div>
              <GalleryUploader
                label="Galerie d'images"
                value={formData.gallery}
                onChange={(arr) => setFormData((prev) => ({ ...prev, gallery: arr }))}
                maxImages={12}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
              />
            </div>

            {formData.status === 'available' && (
              <div>
                <label className="block text-sm font-semibold mb-2">Inclus dans l'offre (une ligne par élément)</label>
                <textarea
                  name="included"
                  value={formData.included}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="Machine révisée&#10;Logiciel&#10;Formation 2h offerte&#10;Garantie 6 mois"
                />
              </div>
            )}

            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#5C3A21' }}
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UsedMachineForm;
